class CartSummaryWidget {
	constructor(widgetElement) {
		if (!widgetElement) {
			return;
		}

		this.widget = widgetElement;
		this.unsubscribers = [];
		this.boundHandleDiscountSubmit = this.handleDiscountSubmit.bind(this);
		this.isSubmittingDiscount = false;
		this.currentCartData = null;

		this.init();
	}

	init() {
		try {
			if (
				typeof subscribe === "function" &&
				typeof PUB_SUB_EVENTS !== "undefined"
			) {
				const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
					if (event.cartData) {
						this.handleCartUpdate(event);
					} else {
						this.fetchCartData();
					}
				});
				this.unsubscribers.push(unsubscribe);
				console.debug("CartSummaryWidget: Subscribed via pub/sub system");
			}

			const discountForm = this.widget.querySelector("#discount-form");
			if (discountForm) {
				discountForm.addEventListener("submit", this.boundHandleDiscountSubmit);
			}

			this.fetchCartData();
		} catch (error) {}
	}

	fetchCartData() {
		fetch(`${window.Shopify?.routes?.root || "/"}cart.js`)
			.then((response) => response.json())
			.then((cart) => {
				this.currentCartData = cart;
				this.updateSummary(cart);
			})
			.catch((error) => {});
	}

	handleDiscountSubmit(event) {
		event.preventDefault();

		if (this.isSubmittingDiscount) {
			return;
		}

		const discountInput = this.widget.querySelector("#discount-code");
		const discountCode = discountInput?.value.trim();
		const errorElement = this.widget.querySelector("#discount-error");

		if (errorElement) {
			errorElement.style.display = "none";
			errorElement.textContent = "";
		}

		if (!discountCode) {
			if (errorElement) {
				errorElement.textContent = "Please enter a discount code";
				errorElement.style.display = "block";
			}
			return;
		}

		this.applyDiscount(discountCode, errorElement, discountInput);
	}

	applyDiscount(discountCode, errorElement, discountInput) {
		this.isSubmittingDiscount = true;

		const body = JSON.stringify({
			discount: discountCode,
		});

		fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: body,
		})
			.then((response) => response.json())
			.then((cart) => {
				this.currentCartData = cart;

				if (discountInput) {
					discountInput.value = "";
				}

				this.updateSummary(cart);

				if (
					typeof publish === "function" &&
					typeof PUB_SUB_EVENTS !== "undefined"
				) {
					publish(PUB_SUB_EVENTS.cartUpdate, {
						source: "cart-summary-widget-discount",
						cartData: cart,
					});
				}

				if (errorElement) {
					errorElement.style.display = "none";
					errorElement.textContent = "";
				}
			})
			.catch((error) => {
				console.error("CartSummaryWidget: Error applying discount", error);

				if (errorElement) {
					errorElement.textContent =
						error.message || "Unable to apply discount code";
					errorElement.style.display = "block";
				}
			})
			.finally(() => {
				this.isSubmittingDiscount = false;
			});
	}

	handleCartUpdate(event) {
		try {
			if (event.source === "cart-summary-widget-discount") {
				return;
			}

			let cartData = event.cartData || event;

			if (!cartData || typeof cartData !== "object") {
				console.warn("CartSummaryWidget: Invalid cart data received", event);
				return;
			}

			this.currentCartData = cartData;
			this.updateSummary(cartData);
		} catch (error) {
			console.error("CartSummaryWidget: Error handling cart update", error);
		}
	}

	updateSummary(cartData) {
		try {
			if (!cartData) {
				console.warn("CartSummaryWidget: No cart data to update");
				return;
			}

			const cartSubtotal = this.parseAmount(cartData.items_subtotal_price);
			let totalDiscount = 0;

			if (
				cartData.cart_level_discount_applications &&
				Array.isArray(cartData.cart_level_discount_applications)
			) {
				totalDiscount = cartData.cart_level_discount_applications.reduce(
					(sum, discount) => {
						const amount = this.parseAmount(discount.total_allocated_amount);
						return sum + amount;
					},
					0,
				);
			}

			const estimatedTotal = Math.max(
				0,
				cartSubtotal - totalDiscount,
			);

			const itemCountEl = document.getElementById("cart-summary-item-count");
			if (itemCountEl) {
				// Calculate item count excluding customizations
				const itemCount = cartData.items.reduce((total, item) => {
					if (!item.properties || !item.properties._is_customization) {
						return total + item.quantity;
					}
					return total;
				}, 0);
				itemCountEl.textContent = String(itemCount || 0);
			}

			const subtotalEl = document.getElementById("cart-summary-subtotal");
			if (subtotalEl) {
				subtotalEl.textContent = this.formatMoney(cartSubtotal);
				subtotalEl.dataset.amount = cartSubtotal;
			}

			this.updateDiscountSection(totalDiscount);

			const totalEl = document.getElementById("cart-summary-total");
			if (totalEl) {
				const strongEl = totalEl.querySelector("strong");
				const formattedTotal = this.formatMoney(estimatedTotal);
				if (strongEl) {
					strongEl.textContent = formattedTotal;
					strongEl.dataset.amount = estimatedTotal;
				} else {
					totalEl.textContent = formattedTotal;
					totalEl.dataset.amount = estimatedTotal;
				}
			}

			this.updateActiveDiscounts(cartData);
		} catch (error) {
			console.error("CartSummaryWidget: Error updating summary", error);
		}
	}

	updateDiscountSection(totalDiscount) {
		try {
			const discountSection = this.widget.querySelector(
				"#cart-summary-discount-section",
			);
			if (!discountSection) return;

			const discountEl = document.getElementById("cart-summary-discount");

			if (totalDiscount > 0) {
				if (discountEl) {
					discountEl.textContent = `-${this.formatMoney(totalDiscount)}`;
					discountEl.dataset.amount = totalDiscount;
				}
				discountSection.style.display = "flex";
			} else {
				discountSection.style.display = "none";
				if (discountEl) {
					discountEl.dataset.amount = "0";
				}
			}
		} catch (error) {
			console.error("CartSummaryWidget: Error updating discount section", error);
		}
	}

	updateActiveDiscounts(cartData) {
		try {
			const activeDiscountsEl = this.widget.querySelector(
				".cart-summary__active-discounts",
			);
			if (!activeDiscountsEl) return;

			const discountsList = activeDiscountsEl.querySelector(".discounts");
			if (!discountsList) return;

			discountsList.innerHTML = "";

			if (
				cartData.cart_level_discount_applications &&
				cartData.cart_level_discount_applications.length > 0
			) {
				cartData.cart_level_discount_applications.forEach((discount) => {
					const li = document.createElement("li");
					li.className = "discounts__discount discounts__discount--position";

					let discountToUse = discount || {};

					if (discount.title	=== undefined) {
						discountToUse = discount.discount_application;
					}
					const iconElement =
						document.querySelector(".discount-svg")?.innerHTML ||
						'<span style="margin-right: 5px;">•</span>';

					li.innerHTML = `
						${iconElement}
						${discountToUse.title || "Discount"}
						(-${this.formatMoney(this.parseAmount(discountToUse.total_allocated_amount))})
					`;
					discountsList.appendChild(li);
				});
				activeDiscountsEl.style.display = "block";
			} else {
				activeDiscountsEl.style.display = "none";
			}
		} catch (error) {
			console.error("CartSummaryWidget: Error updating active discounts", error);
		}
	}

	parseAmount(value) {
		if (typeof value === "number") {
			return isNaN(value) ? 0 : value;
		}

		if (typeof value === "string") {
			const parsed = parseFloat(value.replace(/[^0-9.-]/g, ""));
			return isNaN(parsed) ? 0 : Math.round(parsed);
		}

		return 0;
	}

	formatMoney(cents) {
		try {
			const amount = this.parseAmount(cents);

			const locale = document.documentElement.lang || "en-US";
			const currency = window.Shopify?.currency?.active || "USD";

			return new Intl.NumberFormat(locale, {
				style: "currency",
				currency: currency,
			}).format(amount / 100);
		} catch (error) {
			console.error("CartSummaryWidget: Error formatting money", error);
			const amount = this.parseAmount(cents);
			return `$${(amount / 100).toFixed(2)}`;
		}
	}

	destroy() {
		try {
			this.unsubscribers.forEach((unsubscribe) => {
				if (typeof unsubscribe === "function") {
					unsubscribe();
				}
			});

			const discountForm = this.widget.querySelector("#discount-form");
			if (discountForm) {
				discountForm.removeEventListener("submit", this.boundHandleDiscountSubmit);
			}

			this.widget = null;
		} catch (error) {
			console.error("CartSummaryWidget: Error during cleanup", error);
		}
	}
}

function initCartSummaryWidget() {
	try {
		const cartSummaryWidget = document.getElementById("cart-summary-widget");
		if (!cartSummaryWidget) {
			console.debug("CartSummaryWidget: Widget element not found on page");
			return;
		}

		if (cartSummaryWidget.CartSummaryWidget) {
			console.debug("CartSummaryWidget: Already initialized");
			return;
		}

		const summary = new CartSummaryWidget(cartSummaryWidget);

		cartSummaryWidget.CartSummaryWidget = summary;
		window.CartSummaryWidget = summary;

		window.addEventListener("beforeunload", () => {
			summary.destroy();
		});

		document.addEventListener("shopify:section:unload", () => {
			summary.destroy();
		});
	} catch (error) {
		console.error("CartSummaryWidget: Error during initialization", error);
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initCartSummaryWidget);
} else {
	initCartSummaryWidget();
}

if (typeof module !== "undefined" && module.exports) {
	module.exports = CartSummaryWidget;
}
